import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { Mail, User, Loader, CheckCircle, MapPin, X } from 'lucide-react';
import { GoogleAddressAutocomplete, AddressDetails } from './common/GoogleAddressAutocomplete';
import { PhoneInput } from './common/PhoneInput';
import { Address } from '../types';
import { useTranslation } from '../i18n/hooks/useTranslation';

export const ProfileSetupModal: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  // 2FA is now handled by 3PM Auth, so we only have the profile step
  const [step] = useState<'profile'>('profile');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<Address>({});
  const [addressFormatted, setAddressFormatted] = useState('');
  const [account, setAccount] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [hasShownModal, setHasShownModal] = useState(false); // Track if modal has been shown
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Helper function to convert country name to ISO code
  const getCountryCode = (countryName: string | undefined): string | undefined => {
    if (!countryName) return undefined;
    
    const countryMap: { [key: string]: string } = {
      'new zealand': 'nz',
      'australia': 'au',
      'united states': 'us',
      'united states of america': 'us',
      'usa': 'us',
      'united kingdom': 'gb',
      'uk': 'gb',
      'canada': 'ca',
      'south africa': 'za',
      'ireland': 'ie',
      'singapore': 'sg',
      'malaysia': 'my',
      'philippines': 'ph',
      'indonesia': 'id',
      'thailand': 'th',
      'vietnam': 'vn',
      'japan': 'jp',
      'south korea': 'kr',
      'china': 'cn',
      'india': 'in',
      'france': 'fr',
      'germany': 'de',
      'italy': 'it',
      'spain': 'es',
      'netherlands': 'nl',
      'belgium': 'be',
      'switzerland': 'ch',
      'austria': 'at',
      'sweden': 'se',
      'norway': 'no',
      'denmark': 'dk',
      'finland': 'fi',
      'poland': 'pl',
      'portugal': 'pt',
      'brazil': 'br',
      'mexico': 'mx',
      'argentina': 'ar',
      'chile': 'cl',
      'colombia': 'co',
      'peru': 'pe',
      'uae': 'ae',
      'united arab emirates': 'ae',
      'saudi arabia': 'sa',
      'israel': 'il',
      'turkey': 'tr',
      'egypt': 'eg',
      'nigeria': 'ng',
      'kenya': 'ke'
    };
    
    const normalized = countryName.toLowerCase().trim();
    return countryMap[normalized];
  };

  // Fetch account data to get default country
  useEffect(() => {
    const fetchAccount = async () => {
      if (user?.currentAccountId) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${apiUrl}/api/accounts/${user.currentAccountId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (data.success && data.account) {
            setAccount(data.account);
          }
        } catch (error) {
          console.error('Failed to fetch account:', error);
        }
      }
    };
    fetchAccount();
  }, [user?.currentAccountId, apiUrl]);

  // Check if user needs profile setup - only show if profile is incomplete
  // Show if firstName/lastName are missing or have default values
  // This will only be true on first login since after completing profile, these will be set properly
  // IMPORTANT: Skip this check for SSO users (users with threePMUserId) since their profile
  // data comes from 3PM Auth and is considered authoritative - they've already filled it during registration
  const needsProfileSetup = user && !user.threePMUserId && (
    !user.firstName || 
    !user.lastName || 
    user.firstName === user.email.split('@')[0] || 
    user.lastName === 'User'
  );

  // Once we determine the modal should show, keep it showing even if user data changes
  useEffect(() => {
    if (needsProfileSetup && !hasShownModal) {
      console.log('✅ [ProfileSetupModal] Setting hasShownModal to true');
      setHasShownModal(true);
    }
  }, [needsProfileSetup, hasShownModal]);

  // Debug logging
  useEffect(() => {
    if (user) {
      console.log('🔍 [ProfileSetupModal] User check:', {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        emailPrefix: user.email?.split('@')[0],
        firstNameMatches: user.firstName === user.email?.split('@')[0],
        lastNameIsUser: user.lastName === 'User',
        needsProfileSetup,
        hasShownModal,
        lastLogin: user.lastLogin,
        isPending: user.isPending
      });
    }
  }, [user, needsProfileSetup, hasShownModal]);

  useEffect(() => {
    if (user) {
      // Auto-populate all fields from user data (especially for SSO users who already filled this in 3PM Auth)
      if (user.firstName && user.firstName !== user.email.split('@')[0]) {
        setFirstName(user.firstName);
      }
      if (user.lastName && user.lastName !== 'User') {
        setLastName(user.lastName);
      }
      // Populate phone from either phone or smsPhoneNumber field
      setPhone(user.phone || user.smsPhoneNumber || '');
      // Populate address if available
      if (user.address) {
        setAddress(user.address);
        setAddressFormatted(user.address.formatted || '');
      }
    }
  }, [user]);

  const handleAddressSelect = (addressDetails: AddressDetails) => {
    setAddress({
      formatted: addressDetails.formatted,
      streetNumber: addressDetails.streetNumber,
      streetName: addressDetails.streetName,
      street: addressDetails.street,
      city: addressDetails.city,
      state: addressDetails.state,
      postalCode: addressDetails.postalCode,
      country: addressDetails.country,
      lat: addressDetails.lat,
      lng: addressDetails.lng
    });
    setAddressFormatted(addressDetails.formatted);
  };

  const handleSaveSetup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!user?._id) return;
    
    // Name is required
    if (!firstName || !lastName) {
      setMessage('First name and last name are required.');
      return;
    }

    setIsSaving(true);
    setMessage('');

    try {
      const payload: any = { 
        firstName, 
        lastName 
      };
      
      // Only include phone if provided
      if (phone) {
        payload.phone = phone;
        payload.smsPhoneNumber = phone; // Also set SMS phone number
      }
      
      // Only include address if any address fields are provided
      if (addressFormatted || Object.keys(address).length > 0) {
        payload.address = address;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users/${user._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh user data from server
        await refreshUser();
        
        // Skip 2FA setup since auth is handled by 3PM Auth - close modal and redirect
        setHasShownModal(false);
        navigate('/');
      } else {
        setMessage(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Save setup error:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    // Save minimal required info (name) if not already set
    if (!firstName || !lastName) {
      // Try to get name from email if not provided
      const emailPrefix = user?.email?.split('@')[0] || '';
      setFirstName(emailPrefix);
      setLastName('User');
    }
    
    // Save and proceed
    await handleSaveSetup();
  };

  // Show modal if it should be shown OR if we've already shown it (to prevent flickering)
  const shouldShowModal = (needsProfileSetup || hasShownModal) && user;
  
  if (!shouldShowModal) {
    return null;
  }

  return (
    <>
      {/* Backdrop overlay - blocks entire app */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Complete Your Profile
            </h2>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={isSaving}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            <div className="text-center mb-6">
                  <div className="text-primary-600 mb-4">
                    <CheckCircle className="w-16 h-16 mx-auto" />
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    Let's finish setting up your profile. You can skip optional fields for now.
                  </p>
                </div>

                <form onSubmit={handleSaveSetup} className="space-y-4">
              {/* Email (read-only) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={user.email || ''}
                    disabled
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* First and Last Name on one line */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      placeholder="First name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address - moved below name */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Home Address <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <GoogleAddressAutocomplete
                    value={addressFormatted}
                    onChange={(value) => {
                      setAddressFormatted(value);
                      if (!value) {
                        setAddress({});
                      }
                    }}
                    onPlaceSelect={handleAddressSelect}
                    placeholder="Enter your home address"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  defaultCountry={account?.address?.country ? getCountryCode(account.address.country) : undefined}
                  placeholder="Enter your phone number"
                  className="w-full"
                />
              </div>

              {message && (
                <div className={`p-3 rounded-md text-sm ${
                  message.includes('success') || message.includes('updated')
                    ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
                    : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
                }`}>
                  {message}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isSaving}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Skip for Now
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !firstName || !lastName}
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {isSaving ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save & Continue</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

